import ModalBase from '@/components/base/ModalBase';
import type { PaymentProcessor } from '@/mocks/superAdminPaymentProcessors';

interface Props {
  open: boolean;
  onClose: () => void;
  processor: PaymentProcessor | null;
}

export default function ProcessorDetailModal({ open, onClose, processor }: Props) {
  if (!processor) return null;

  const isApi = processor.category === 'api';
  const commissionDisplay =
    processor.commissionPercent > 0 || processor.commissionFixed > 0
      ? `${processor.commissionPercent}% + $${processor.commissionFixed.toFixed(2)}`
      : 'Sin comisión';

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title={processor.name}
      subtitle={isApi ? 'Procesador de pago API' : 'Método de pago manual'}
      statusLabel={processor.enabled ? 'Activo' : 'Inactivo'}
      statusColor={processor.enabled ? 'bg-emerald-50 text-emerald-600' : 'bg-background-100 text-foreground-400'}
      size="lg"
      headerIcon={
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${processor.logoColor}`}>
          <i className={`${processor.icon} text-sm`}></i>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Summary grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-medium">Estado</p>
            <p className={`text-xs font-bold mt-0.5 ${processor.enabled ? 'text-emerald-600' : 'text-foreground-400'}`}>
              {processor.enabled ? 'Conectado' : 'Desconectado'}
            </p>
          </div>
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-medium">Modo</p>
            <p className={`text-xs font-bold mt-0.5 ${processor.testMode ? 'text-amber-600' : 'text-emerald-600'}`}>
              {processor.testMode ? 'Test' : 'Producción'}
            </p>
          </div>
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-medium">Comisión</p>
            <p className="text-xs font-bold mt-0.5 text-foreground-800">{commissionDisplay}</p>
          </div>
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-medium">Predeterminado</p>
            <p className={`text-xs font-bold mt-0.5 ${processor.isDefault ? 'text-accent-600' : 'text-foreground-400'}`}>
              {processor.isDefault ? 'Sí' : 'No'}
            </p>
          </div>
        </div>

        {/* API details */}
        {isApi && (
          <div>
            <p className="text-foreground-700 text-[10px] font-semibold mb-2">Credenciales</p>
            <div className="space-y-1.5 bg-background-100 rounded-xl p-3">
              {processor.config.map((cfg) => (
                <div key={cfg.key} className="flex items-center justify-between gap-3">
                  <span className="text-foreground-400 text-[10px] shrink-0">{cfg.label}</span>
                  <span className="text-foreground-800 text-[10px] font-mono truncate text-right">
                    {cfg.type === 'password' ? '••••••••••••••••' : cfg.value || '—'}
                  </span>
                </div>
              ))}
              {processor.webhookStatus && (
                <div className="flex items-center justify-between gap-3 pt-1.5 border-t border-background-200">
                  <span className="text-foreground-400 text-[10px] shrink-0">Webhook</span>
                  <span className={`text-[10px] font-semibold ${processor.webhookStatus === 'healthy' ? 'text-emerald-500' : processor.webhookStatus === 'failing' ? 'text-red-400' : 'text-foreground-400'}`}>
                    {processor.webhookStatus === 'healthy' ? 'Saludable' : processor.webhookStatus === 'failing' ? 'Fallando' : 'Sin configurar'}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Manual details */}
        {!isApi && (
          <div>
            <p className="text-foreground-700 text-[10px] font-semibold mb-2">Información de cobro</p>
            <div className="bg-background-100 rounded-xl p-3 space-y-2">
              {processor.receiverName && (
                <div className="flex items-center justify-between gap-3">
                  <span className="text-foreground-400 text-[10px] shrink-0">Receptor</span>
                  <span className="text-foreground-800 text-[10px] font-semibold">{processor.receiverName}</span>
                </div>
              )}
              {processor.receiverContact && (
                <div className="flex items-center justify-between gap-3">
                  <span className="text-foreground-400 text-[10px] shrink-0">Contacto</span>
                  <span className="text-foreground-800 text-[10px] font-semibold">{processor.receiverContact}</span>
                </div>
              )}
              {processor.manualInstructions && (
                <div className="pt-2 border-t border-background-200">
                  <span className="text-foreground-400 text-[9px] block mb-1">Instrucciones</span>
                  <pre className="text-foreground-600 text-[10px] font-mono whitespace-pre-wrap leading-relaxed">{processor.manualInstructions}</pre>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Last activity */}
        <div className="grid grid-cols-2 gap-2">
          {processor.lastSync && (
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[9px] font-medium">Última sincronización</p>
              <p className="text-foreground-800 text-xs font-bold mt-0.5">{processor.lastSync}</p>
            </div>
          )}
          {processor.lastTest && (
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[9px] font-medium">Última prueba</p>
              <p className="text-foreground-800 text-xs font-bold mt-0.5">{processor.lastTest}</p>
            </div>
          )}
          {!processor.lastSync && !processor.lastTest && (
            <div className="bg-background-100 rounded-xl p-3 col-span-2">
              <p className="text-foreground-400 text-xs text-center">Sin actividad registrada</p>
            </div>
          )}
        </div>

        {/* Usage functions */}
        {processor.usageFunctions && processor.usageFunctions.length > 0 && (
          <div>
            <p className="text-foreground-700 text-[10px] font-semibold mb-2">
              Dónde se usa ({processor.usageFunctions.filter((f) => f.enabled).length}/{processor.usageFunctions.length})
            </p>
            <div className="bg-background-100 rounded-xl p-3 space-y-1.5">
              {processor.usageFunctions.map((fn) => (
                <div key={fn.id} className="flex items-center justify-between gap-3">
                  <span className={`text-[10px] ${fn.enabled ? 'text-foreground-700' : 'text-foreground-300'}`}>
                    {fn.label}
                  </span>
                  {fn.enabled
                    ? <span className="text-emerald-500 text-[10px]"><i className="ri-check-line"></i></span>
                    : <span className="text-foreground-300 text-[10px]"><i className="ri-close-line"></i></span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Permissions */}
        <div>
          <p className="text-foreground-700 text-[10px] font-semibold mb-2">Permisos ({processor.permissions.filter((p) => p.enabled).length}/{processor.permissions.length})</p>
          <div className="bg-background-100 rounded-xl p-3 space-y-1.5">
            {processor.permissions.map((perm) => (
              <div key={perm.label} className="flex items-center justify-between gap-3">
                <span className="text-foreground-700 text-[10px]">{perm.label}</span>
                {perm.enabled
                  ? <span className="text-emerald-500 text-[10px]"><i className="ri-check-line"></i></span>
                  : <span className="text-foreground-300 text-[10px]"><i className="ri-close-line"></i></span>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <ModalBase
        open={false}
        onClose={() => {}}
        title=""
        footer={
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-background-200 text-foreground-600 text-xs font-bold hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cerrar
          </button>
        }
      />
    </ModalBase>
  );
}